import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { FaTiktok, FaInstagram, FaLinkedin, FaWhatsapp, FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa';
import './ContactUs.css';

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [visibleElements, setVisibleElements] = useState(new Set());

  const observerRef = useRef();

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const timer = setTimeout(() => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach(el => observerRef.current.observe(el));
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitMessage(null);

    try {
      // Save contact message to Firestore
      const contactsCol = collection(db, 'contacts');
      await addDoc(contactsCol, {
        name: formData.name,
        email: formData.email,
        message: formData.message,
        createdAt: serverTimestamp()
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        message: ''
      });

      setSubmitMessage('Your message has been sent successfully!');

    } catch (error) {
      console.error("Error submitting contact message:", error);
      setSubmitMessage(error?.message || 'Error sending message. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="hero-content">
          <h1 className={`animate-on-scroll ${visibleElements.has('contact-hero-title') ? 'animate-fade-in-down' : ''}`} id="contact-hero-title">Get In Touch</h1>
          <p className={`hero-subtitle animate-on-scroll ${visibleElements.has('contact-hero-subtitle') ? 'animate-fade-in-up' : ''}`} id="contact-hero-subtitle">Let's create something extraordinary together. We'd love to hear about your next project.</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className={`contact-form-section animate-on-scroll ${visibleElements.has('contact-form') ? 'animate-slide-in-left' : ''}`} id="contact-form">
              <div className="form-header">
                <h2>Send Us a Message</h2>
                <p>Fill out the form below and we'll get back to you as soon as possible.</p>
              </div>

              {submitMessage && (
                <div className={`submit-message ${submitMessage.includes('successfully') ? 'success' : 'error'}`}>
                  {submitMessage}
                </div>
              )}

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <input 
                    type="text" 
                    name="name"
                    placeholder="Your Name" 
                    className="form-input" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <input 
                    type="email" 
                    name="email"
                    placeholder="Your Email Address" 
                    className="form-input" 
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <textarea 
                    name="message"
                    placeholder="Tell us about your project..." 
                    rows="6" 
                    className="form-textarea"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>
                <button type="submit" className="form-button" disabled={submitting}>
                  {submitting ? 'Sending Message...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className={`contact-info-section animate-on-scroll ${visibleElements.has('contact-info') ? 'animate-slide-in-right' : ''}`} id="contact-info">
              <div className="info-header">
                <h2>Contact Information</h2>
                <p>Reach out to us through any of these channels.</p>
              </div>

              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="contact-text">
                    <h4>Our Locations</h4>
                    <p>Jeddah, Saudi Arabia</p>
                    <p>Dubai, UAE</p>
                    <p>United States</p>
                    <p>Russia</p>
                    <p>Kazakhstan</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <FaPhone />
                  </div>
                  <div className="contact-text">
                    <h4>Phone & WhatsApp</h4>
                    <p>+966 54 333 0505</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <FaEnvelope />
                  </div>
                  <div className="contact-text">
                    <h4>Email Address</h4>
                    <p>Info@lucamiro.me</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <FaClock />
                  </div>
                  <div className="contact-text">
                    <h4>Business Hours</h4>
                    <p>Sunday - Thursday: 9:00 AM - 6:00 PM</p>
                    <p>Friday - Saturday: Closed</p>
                  </div>
                </div>
              </div>

              <div className="social-section">
                <h3>Follow Us</h3>
                <div className="social-icons">
                  <a href="https://www.tiktok.com/@studio13.sa?_r=1&_t=ZS-95LB16RD0z2" className="social-link tiktok" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                    <FaTiktok />
                  </a>
                  <a href="https://www.instagram.com/studio13.sa?igsh=eXQzbXJ3cHk0emZz" className="social-link instagram" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <FaInstagram />
                  </a>
                  <a href="https://www.linkedin.com/company/luca-miro/" className="social-link linkedin" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                    <FaLinkedin />
                  </a>
                  <a href="https://wa.me/966543330505" className="social-link whatsapp" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                    <FaWhatsapp />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
